import { BookOpen, CircleCheck, ListChecks, UserRound } from 'lucide-react';

import { useCapability } from '../../../../hooks/use-capability';
import { useIntent, useSequenceTimeline } from '../../hooks/use-panel-fixtures';

import { IntentCard } from './IntentCard';
import { PanelSection } from './PanelSection';
import { PersonHistoryBlock } from './PersonHistoryBlock';
import { SequenceTimelineBlock } from './SequenceTimelineBlock';

import type { ConversationDetail } from '../../../../lib/types';

/**
 * Aba "Conversa" — como a conversa é conduzida (Intent ADR-108 + Playbook +
 * Persona) e como ela se conecta (SequenceTimeline ADR-055), agrupadas por
 * subgroup-heads do padrão r7b.
 *
 * Playbook/Persona ativos vêm do detail do Engine (playbookId FK Fatia 11.2 +
 * activePersonaId ?? persona do playbook). Status/modo vivem no header e a
 * sessão Meta no IdentityPopover — removidos daqui (redundância).
 */
export function TabConversa({ conversation }: { conversation: ConversationDetail }): JSX.Element {
  const { intent } = useIntent(conversation);
  const {
    nodes,
    totalConversations,
    isLoading: sequenceLoading,
  } = useSequenceTimeline(conversation);
  // Frente C — navegação usa o id ESCALAR (personId), não o ref expandido: se o
  // serializer entregar personId sem `person` (classe «vínculo mudo»), o atalho
  // continua funcionando.
  const historyPersonId = conversation.personId ?? conversation.person?.id ?? null;
  // P19: sem `person-read` a seção Histórico SOME inteira (nunca casca vazia —
  // o mesmo padrão do botão do header e do gate da ficha). Revisão F.5 r2.
  const canViewPersonHistory = useCapability('person-read');
  const playbook = conversation.playbook ?? null;
  const persona = conversation.persona ?? null;

  return (
    <div className="flex flex-col">
      <div className="subgroup-head">
        <span>Como esta conversa é conduzida</span>
      </div>

      <PanelSection title="Intenção" eyebrow="detectada pela IA">
        <IntentCard intent={intent} />
      </PanelSection>

      <PanelSection
        title="Playbook ativo"
        {...(playbook !== null
          ? {
              eyebrow: `${playbook.name} · ${playbook.steps.length.toString()} etapa${playbook.steps.length === 1 ? '' : 's'}`,
            }
          : {})}
      >
        {playbook === null ? (
          <div className="empty-mini">
            <BookOpen className="icon" size={14} aria-hidden />
            Nenhum playbook ativo nesta conversa.
          </div>
        ) : (
          <>
            <div className="sub-group__head">
              <ListChecks size={11} aria-hidden /> Etapas
            </div>
            <ol
              style={{
                margin: 0,
                paddingLeft: 'var(--space-4)',
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                fontSize: 'var(--text-xs)',
                color: 'var(--fg-2)',
              }}
            >
              {playbook.steps.map((step) => (
                <li
                  key={step.id}
                  style={step.handled === true ? { color: 'var(--fg-1)' } : undefined}
                >
                  {/* Progresso conservador (spec 2026-07-18): o check só aparece
                      quando o Engine COMPROVOU a etapa tratada (handled === true).
                      Ausência = não afirmado, renderiza neutro — nunca "não feito".
                      Flex vai no wrapper interno (não no <li>) p/ preservar o número
                      da <ol> (N1 da revisão). */}
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    {step.handled === true && (
                      <CircleCheck
                        size={12}
                        role="img"
                        aria-label="etapa tratada"
                        style={{ color: 'var(--success-fg)', flexShrink: 0 }}
                      />
                    )}
                    <span>{step.name}</span>
                  </span>
                </li>
              ))}
            </ol>
          </>
        )}
      </PanelSection>

      <PanelSection title="Persona ativa" {...(persona !== null ? { eyebrow: persona.name } : {})}>
        {persona === null ? (
          <div className="empty-mini">
            <UserRound className="icon" size={14} aria-hidden />
            Nenhuma persona ativa nesta conversa.
          </div>
        ) : (
          <dl className="kv-list">
            {joinValues(persona.voiceProfile) !== null && (
              <div className="kv-list__row">
                <dt>Voz</dt>
                <dd>{joinValues(persona.voiceProfile)}</dd>
              </div>
            )}
            {joinList(persona.ethicalBoundaries.redirectTopics) !== null && (
              <div className="kv-list__row">
                <dt>Fronteira</dt>
                <dd>{joinList(persona.ethicalBoundaries.redirectTopics)}</dd>
              </div>
            )}
            {joinList(persona.ethicalBoundaries.toneLimits) !== null && (
              <div className="kv-list__row">
                <dt>Tom</dt>
                <dd>{joinList(persona.ethicalBoundaries.toneLimits)}</dd>
              </div>
            )}
          </dl>
        )}
      </PanelSection>

      <div className="subgroup-head">
        <span>Como ela se conecta</span>
      </div>

      {/* B3 (spec 2026-08-12 §3.2) — o histórico REAL do cliente. */}
      {canViewPersonHistory && (
        <PanelSection title="Histórico do cliente" eyebrow="todas as conversas desta pessoa">
          {historyPersonId === null ? (
            <div className="empty-mini">Cliente não identificado — sem histórico para mostrar.</div>
          ) : (
            <PersonHistoryBlock conversation={conversation} />
          )}
        </PanelSection>
      )}

      {/* Sequência (ADR-055) é EPISÓDIO operacional (janela 24h) — só faz
          sentido com Bond; sem ele o hook é inerte e a seção some (o texto
          antigo aqui AFIRMAVA «primeira conversa» — mentira medida 12/Ago). */}
      {conversation.bondId !== null && (
        <PanelSection
          title="Sequência de conversas"
          eyebrow="episódio recente neste vínculo (24h)"
          count={totalConversations}
        >
          {sequenceLoading ? (
            <p className="placeholder-text">Carregando sequência…</p>
          ) : (
            <SequenceTimelineBlock nodes={nodes} />
          )}
        </PanelSection>
      )}
    </div>
  );
}

/** `voiceProfile` é Json livre ({formality, warmth, pace}) — junta os valores string. */
function joinValues(obj: Record<string, unknown>): string | null {
  const values = Object.values(obj).filter((v): v is string => typeof v === 'string');
  return values.length > 0 ? values.join(' · ') : null;
}

/** Lista Json livre (redirectTopics/toneLimits) — junta strings; vazia/malformada → null. */
function joinList(value: unknown): string | null {
  if (!Array.isArray(value)) return null;
  const items = value.filter((v): v is string => typeof v === 'string');
  return items.length > 0 ? items.join(' · ') : null;
}
